import * as React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import ProviderMatchEventInfoDto from '../models/dtos/ProviderMatchEventInfoDto';
import ProviderLogTable from './ProviderLogTable';

interface ProviderMatchEventInfoTablesProps {
    info?: ProviderMatchEventInfoDto | null;
    loading?: boolean;
    showLogs?: boolean;
    title?: string;
}

const renderCopyableHeader = (
    label: string,
    value?: string | number | null,
    copiedKey?: string | null,
    onCopy?: (key: string, value?: string | number | null) => void,
) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>{label}</span>
        <Tooltip title={copiedKey === label ? 'Copied!' : `Copy ${label}`}>
            <span>
                <IconButton
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        onCopy?.(label, value);
                    }}
                    sx={{
                        padding: '2px',
                        '&:hover': { backgroundColor: 'action.hover' }
                    }}
                >
                    <ContentCopyIcon
                        fontSize="small"
                        sx={{ color: copiedKey === label ? 'success.main' : 'action.active' }}
                    />
                </IconButton>
            </span>
        </Tooltip>
    </Box>
);

const getColumnsFirst = (
    info: ProviderMatchEventInfoDto,
    copiedKey: string | null,
    onCopy: (key: string, value?: string | number | null) => void,
): GridColDef[] => [
    {
        field: 'id',
        headerName: 'id',
        width: 300,
        renderHeader: () => renderCopyableHeader('id', info.id, copiedKey, onCopy)
    },
    {
        field: 'sport.name', headerName: '', width: 150, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.sportName}</React.Fragment>
    },
    {
        field: 'league.name', headerName: 'League', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.leagueName}</React.Fragment>
    },
    {
        field: 'home', headerName: 'Home', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.homeTeamName}</React.Fragment>
    },
    {
        field: 'away', headerName: 'Away', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.awayTeamName}</React.Fragment>
    },
    {
        field: 'startDate', headerName: 'Date', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{new Date(params.row.startDate).toLocaleString()}</React.Fragment>
    }
];

const columnsSecond: GridColDef[] = [
    {
        field: 'matchState', headerName: 'MatchState', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.parentMatchState}</React.Fragment>
    },
    {
        field: 'closed', headerName: 'Closed', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.isClosed.toString()}</React.Fragment>
    },
    {
        field: 'finished', headerName: 'Finished', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.isFinished.toString()}</React.Fragment>
    },
    {
        field: 'cancelled', headerName: 'Cancelled', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.isBadMatch.toString()}</React.Fragment>
    },
    {
        field: 'isInResponse', headerName: 'InResponse', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.isInResponse.toString()}</React.Fragment>
    },
    {
        field: 'lastRefresh', headerName: 'Last time in response', width: 250, renderCell: (params: GridCellParams) =>
            <React.Fragment>{new Date(params.row.lastTimeRefresh).toLocaleString()}</React.Fragment>
    }
];

const getColumnsThird = (
    info: ProviderMatchEventInfoDto,
    copiedKey: string | null,
    onCopy: (key: string, value?: string | number | null) => void,
): GridColDef[] => [
    {
        field: 'xbetId',
        headerName: 'ProviderId',
        width: 170,
        renderHeader: () => renderCopyableHeader('ProviderId', info.xbetId, copiedKey, onCopy),
        renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.xbetId}</React.Fragment>
    },
    {
        field: 'xbetStatId',
        headerName: 'Provider stats id',
        width: 220,
        renderHeader: () => renderCopyableHeader('Provider stats id', info.xbetStatId, copiedKey, onCopy),
        renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.xbetStatId ?? ''}</React.Fragment>
    },
    {
        field: 'constXbetId',
        headerName: 'ConstProviderId',
        width: 190,
        renderHeader: () => renderCopyableHeader('ConstProviderId', info.constXbetId, copiedKey, onCopy),
        renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.constXbetId}</React.Fragment>
    },
    {
        field: 'matchId',
        headerName: 'MatchId',
        width: 320,
        renderHeader: () => renderCopyableHeader('MatchId', info.parentMatchId, copiedKey, onCopy),
        renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.parentMatchId}</React.Fragment>
    },
    {
        field: 'scoreHome', headerName: 'ScoreHome', width: 100, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.scoreHome}</React.Fragment>
    },
    {
        field: 'scoreAway', headerName: 'ScoreAway', width: 100, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.scoreAway}</React.Fragment>
    },
    {
        field: 'rawScore', headerName: 'Raw', width: 300, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.resultRaw}</React.Fragment>
    },
    {
        field: 'redCards', headerName: 'RedCards', width: 100, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.redCards}</React.Fragment>
    },
    {
        field: 'penalties', headerName: 'Penalties', width: 100, renderCell: (params: GridCellParams) =>
            <React.Fragment>{params.row.penalties}</React.Fragment>
    }
];

const baseGridProps = {
    autoHeight: true,
    initialState: {
        pagination: {
            paginationModel: { page: 0, pageSize: 25 },
        },
    },
    pageSizeOptions: [5, 10],
    hideFooterPagination: true,
    hideFooter: true
};

export default function ProviderMatchEventInfoTables(props: ProviderMatchEventInfoTablesProps) {
    const [copiedKey, setCopiedKey] = React.useState<string | null>(null);

    const handleCopy = React.useCallback(async (key: string, value?: string | number | null) => {
        if (value == null || value === '') {
            return;
        }

        try {
            await navigator.clipboard.writeText(value.toString());
            setCopiedKey(key);
            window.setTimeout(() => setCopiedKey(current => current === key ? null : current), 1500);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    }, []);

    if (props.loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', py: 3 }}>
                {props.title != null && <Typography variant="h6" sx={{ mb: 2 }}>{props.title}</Typography>}
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (props.info == null) {
        return null;
    }

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            {props.title != null && <Typography variant="h6">{props.title}</Typography>}
            <DataGrid sx={{ width: '100%' }} rows={[props.info]} columns={getColumnsFirst(props.info, copiedKey, handleCopy)} {...baseGridProps} />
            <DataGrid sx={{ width: '100%' }} rows={[props.info]} columns={columnsSecond} {...baseGridProps} />
            <DataGrid sx={{ width: '100%' }} rows={[props.info]} columns={getColumnsThird(props.info, copiedKey, handleCopy)} {...baseGridProps} />
            {props.showLogs && (
                <Box>
                    <ProviderLogTable log={props.info.logs ?? []} />
                </Box>
            )}
        </Stack>
    );
}
